import { DataSource } from '@angular/cdk/collections';
import { CollectionViewer } from '@angular/cdk/collections';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DomSanitizer } from '@angular/platform-browser';
import Dropzone from 'dropzone';
import { difference, orderBy } from 'lodash';
import * as moment from 'moment';
import { BehaviorSubject, filter, fromEvent, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ServiceEventEnums } from '../../enums';
import { Service } from '../../models';
import { ServiceResponse } from '../../models/http-response/service-reponse';
import { AuthenticationService, DialogService, ServicesService, ProcessorService, SnackbarService } from '../../services';
import { JobUploaderService } from '../../services/standard/job-uploader.service';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { JobUploadItemComponent } from './job-upload-item/job-upload-item.component';

@Component({
  selector: 'prism-uploader-modal',
  templateUrl: './uploader-modal.component.html',
  styleUrls: ['./uploader-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class UploaderModalComponent implements OnInit, OnDestroy, AfterViewInit {
  subDestroyer$ = new Subject();
  deliveryRef: { [serviceId: string]: { lastDateDelivered: string, canDeliver: boolean } } = {};
  searchAddressListener = new BehaviorSubject(null);
  serviceUploadDeleteInProgress = {};
  deleteSelectionCollection = {};
  services = new ServicesDataSource(this.servicesService, this.changeRef, this.processorService);
  previewContainerArray = [];
  globalDeliveryPending = false;
  JobUploaded = false;
  localRefSelectedJob: Service;
  allServices$: Observable<any[]> = this.servicesService.services$
    .pipe(
      map(service => orderBy(service, service => service.received, 'desc'))
    );
  allJobsSub: Subscription;
  fileDropzone: Dropzone;
  allUploadsSub: Subscription;
  jobDeliverPendingTracker = {};
  dZoneConfig: Dropzone.DropzoneOptions = {
    url: '#',
    method: 'PUT',
    autoQueue: true,
    maxThumbnailFilesize: 60,
    uploadMultiple: true,
    autoProcessQueue: false,
    createImageThumbnails: true,
    chunking: false
  };

  @ViewChild('dzPreview', {static: true}) dzPreview: ElementRef;
  @ViewChild('dropzone', {static: true}) dropzone: ElementRef<HTMLDivElement>;
  @ViewChild('dropzoneMessage', {static: true}) dropzoneMessage: ElementRef;
  @ViewChildren('thumbnail') thumbnails: ElementRef[];
  @ViewChildren('uploadItems') uploadItems: QueryList<JobUploadItemComponent>;
  @ViewChild('uploadImagesContainer') uploadImagesContainer: ElementRef<HTMLDivElement>;
  @ViewChild('uploaderItemPane', {static: true}) uploaderItemPane: ElementRef<HTMLDivElement>;
  @ViewChild('searchAddressInput', {static: true}) searchAddressInput: ElementRef<HTMLInputElement>;

  constructor(private servicesService: ServicesService,
              private processorService: ProcessorService,
              private authService: AuthenticationService,
              private sanitizer: DomSanitizer,
              private dialogService: DialogService,
              private changeRef: ChangeDetectorRef,
              private jobUploaderService: JobUploaderService,
              private snackbarService: SnackbarService) {
  }

  ngOnInit() {
    this.jobUploaderService.currentJobUploader.subscribe(x => {
      this.JobUploaded = x;
    });

    const {service}: { service: Service } = this.dialogService.inputData;
    if (service && service?.id) {
      this.setSelectedUploaderService(service);

      let searchString: string;
      if (!service?.id) {
        searchString = '';
      }

      if (service?.id.length > 6) {
        const startIndex = service?.id.length - 6;
        searchString = service?.id.substr(startIndex);
      }
      this.services.setSearchString(searchString);
      this.localRefSelectedJob = service;
    } else {
      this.allJobsSub = this.allServices$.subscribe(
        services => {
          if (services[0] !== null) {
            const sortedJobs = services.sort((a, b) => {
              return +b.received - +a.received;
            });
            this.setSelectedUploaderService(sortedJobs[0]);
            this.localRefSelectedJob = sortedJobs[0];
          }
        },
        err => console.error(err),
        () => this.allJobsSub.unsubscribe()
      );
    }

    this.searchAddressListener
      .pipe(
        distinctUntilChanged(),
        filter(text => {
          if (text) {
            return text.length > 1;
          }
          return false;
        }),
        switchMap((searchString) => {
          const searchTags = searchString
            .trim()
            .toLowerCase()
            .replace(/[^a-zA-Z0-9_\-/]+/g, ' ')
            .split(' ')
            .filter(term => !!term);

          return this.servicesService.getSearchedServiceDetails({searchTags})
            .pipe(
              switchMap((servicesResponse: ServiceResponse) => {
                const services = Object.values(servicesResponse.data);
                this.searchAddressInput.nativeElement.value = searchString;
                this.services.setSearchResults(services, searchString);

                this.setSelectedUploaderService(services[0]);
                const finalDirectories = Object.values(servicesResponse.data).map((service: any) => service.fromDirectory);
                return this.servicesService.getServicesFinals({finalDirectories})
                  .pipe(
                    map(urlsResponse => {
                      return [servicesResponse, urlsResponse];
                    })
                  );
              })
            );
        })
      )
      .subscribe(([servicesResponse, urlsResponse]) => {
        const services = servicesResponse.data;
        const finals = urlsResponse.data;
        this.services.setSearchResultFinals(services, finals);
      });
  }

  ngAfterViewInit() {

    const {service}: { service: Service } = this.dialogService.inputData;

    if (service && service?.id) {

      let searchString: string;
      if (!service?.id) {
        searchString = '';
      }

      if (service?.id.length > 6) {
        const startIndex = service?.id.length - 6;
        searchString = service?.id.substr(startIndex);
      }
      this.searchAddressListener.next(searchString);

    } else {
      this.services.requestViewUpdate$
        .subscribe((service) => {
          this.setSelectedUploaderService(service);
        });
    }

    fromEvent(this.searchAddressInput.nativeElement, 'keyup')
      .pipe(
        takeUntil(this.subDestroyer$),
        map((e: KeyboardEvent) => (e.target as HTMLInputElement).value),
        debounceTime(500),
        distinctUntilChanged(),
        filter(text => {
          return text.length > 1;
        })
      )
      .subscribe(() => {
        this.searchAddressListener.next(this.searchAddressInput.nativeElement.value);
      });

    this.dZoneConfig.addedfile = (file: Dropzone.DropzoneFile) => {
      const {address, fromDirectory, id: jobId} = this.localRefSelectedJob;
      const street = address.substr(0, address.indexOf(',')).split(' ').join('_');
      file['jobId'] = jobId;
      file['fromDirectory'] = fromDirectory;
      file['street'] = street;
    };
    this.fileDropzone = new Dropzone(this.dropzone.nativeElement, this.dZoneConfig);
    this.fileDropzone.accept = ((file: any, done) => {
      const fileType = file.type;
      const isPdfFile = fileType.includes('pdf');
      const isAiFile = fileType.includes('postscript');
      const isMiscType =
        fileType.includes('video')
        || (fileType.includes('image') && !fileType.includes('jpeg') && !fileType.includes('png') && !fileType.includes('jpg') && !fileType.includes('webp'))
        || (fileType.includes('photoshop'))
        || (!fileType.includes('video') && !fileType.includes('image') && !isAiFile && !isPdfFile);

      if (isMiscType) {
        this.handleFileUpload(file, '../../../assets/images/no_image.jpg');
      } else if (isPdfFile) {
        this.handleFileUpload(file, '../../../assets/images/pdf-logo.jpeg');
      } else if (isAiFile) {
        this.handleFileUpload(file, '../../../assets/images/ai-logo.png');
      } else {
        this.handleFileUpload(file);
      }

      done();
    });
    this.fileDropzone.on('dragover', data => this.dropzone.nativeElement.classList.add('drag-over'));
    this.fileDropzone.on('dragleave', data => this.dropzone.nativeElement.classList.remove('drag-over'));
    this.fileDropzone.on('drop', (dragEvent: DragEvent) => this.dropzone.nativeElement.classList.remove('drag-over'));
  }

  ngOnDestroy() {
    if (this.allUploadsSub) {
      this.allUploadsSub.unsubscribe();
    }

    this.subDestroyer$.next(null);
  }

  setSelectedUploaderService(service: Service) {
    this.previewContainerArray.forEach((previewContainer: HTMLDivElement) => {
      previewContainer.hidden = !previewContainer.classList.contains(`${service.id}`);
    });
    this.localRefSelectedJob = service;
    this.jobUploaderService.setSelectedUploaderService(service);
    this.services.setSelectedService(service);
  }

  closeDialog() {
    this.dialogService.closeDialog();
  }

  setJobDeliverPending(jobDeliveryPending: { [jobId: string]: boolean }) {
    const deliveryKey = Object.keys(jobDeliveryPending)[0];
    this.jobDeliverPendingTracker[deliveryKey] = jobDeliveryPending[deliveryKey];
    const deliveryValues = Object.values(this.jobDeliverPendingTracker);
    const deliveryBusy = deliveryValues.find(item => item === true);
    this.globalDeliveryPending = !!deliveryBusy;
    this.JobUploaded = true;
    this.jobUploaderService.updateServiceUploaded(true);
  }

  makeTrustedImage(item) {
    const imageString = JSON.stringify(item).replace(/\\n/g, '');
    const style = 'url(' + imageString + ')';
    return this.sanitizer.bypassSecurityTrustStyle(style);
  }

  // contentType -> icon file in assets/icons/asset-type. Real photo types (jpeg/png/webp) are
  // deliberately excluded so they keep showing the actual uploaded thumbnail instead of an icon.
  private readonly assetTypeIconMap: { [contentType: string]: string } = {
    'application/pdf': 'PDF.svg',
    'application/msword': 'DOC.svg',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOC.svg',
    'application/vnd.oasis.opendocument.text': 'DOC.svg',
    'application/vnd.ms-powerpoint': 'PPT.svg',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPT.svg',
    'application/vnd.ms-excel': 'XLS.svg',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLS.svg',
    'audio/mpeg': 'MP3.svg',
    'audio/mp3': 'MP3.svg',
    'video/mp4': 'MP4.svg',
    'image/svg+xml': 'SVG.svg',
    'application/postscript': 'AI.svg',
    'application/illustrator': 'AI.svg',
    'text/plain': 'TXT.svg',
    'application/zip': 'ZIP.svg',
    'application/x-zip-compressed': 'ZIP.svg',
    'image/vnd.adobe.photoshop': 'PSD.svg',
    'application/x-photoshop': 'PSD.svg',
    'image/x-psd': 'PSD.svg',
    'image/gif': 'GIF.svg',
    'video/x-msvideo': 'AVI.svg',
    'video/avi': 'AVI.svg',
    'text/csv': 'CSV.svg',
    'image/tiff': 'TIFF.svg'
  };

  getAssetKind(image: { contentType?: string, url?: string }): 'image' | 'icon' | 'video' | 'none' {
    const contentType = image?.contentType?.toLowerCase();

    if ((contentType && ['image/jpeg', 'image/png', 'image/webp'].includes(contentType)) || image?.url?.startsWith('data:')) {
      return 'image';
    }
    if (contentType && this.assetTypeIconMap[contentType]) {
      return 'icon';
    }
    if (contentType?.startsWith('video')) {
      return 'video';
    }

    return 'none';
  }

  getAssetTypeIconPath(image: { contentType?: string }): string {
    const contentType = image?.contentType?.toLowerCase() || '';
    return `../../../assets/icons/asset-type/${this.assetTypeIconMap[contentType]}`;
  }

  handleFileUpload(file: any, thumbUrl?: string) {
    const originalJob = this.localRefSelectedJob;
    this.jobUploaderService.setCurrentUpload(null, 0, false, file.name, file.upload.uuid, file.jobId);
    const previewContainer = document.createElement('div');
    const previewImg = document.createElement('div');
    previewContainer.classList.add(`${file.jobId}`);
    const progressElement = <HTMLProgressElement>document.createElement('progress');

    progressElement.max = 100;
    previewContainer.classList.add('preview-container');
    previewImg.classList.add('preview-img');
    previewContainer.classList.add('preview-uploading');
    previewContainer.insertAdjacentElement('beforeend', previewImg);
    previewContainer.insertAdjacentElement('beforeend', progressElement);

    previewContainer.hidden = !previewContainer.classList.contains(`${this.localRefSelectedJob.id}`);

    this.uploadImagesContainer.nativeElement.insertAdjacentElement('beforeend', previewContainer);
    this.previewContainerArray.push(previewContainer);

    const fileName = `${moment().valueOf()}_${file.name}`;

    // Get presigned URL for upload
    this.jobUploaderService.getPresignedUploadUrl({
      fileName: fileName,
      contentType: file.type,
      contentLength: file.size,
      serviceId: this.localRefSelectedJob.id,
      processorUid: this.authService.currentProcessorUid
    }).subscribe({
      next: (response) => {
        // Upload file to presigned URL with progress tracking
        this.jobUploaderService.uploadToPresignedUrl(
          response.data.presignedUrl,
          file,
          this.authService.currentProcessorUid,
          (uploadProgress) => {
            progressElement.value = uploadProgress;
            this.jobUploaderService.setCurrentUpload(null, uploadProgress, false, file.name, file.upload.uuid, file.jobId);
          },
          file.upload.uuid
        ).then(() => {
          // Upload completed successfully
          previewContainer.remove();

          if (!originalJob.imagesToDeliver) {
            originalJob.imagesToDeliver = [];
          }

          // For videos and other files, get thumbnail URL for proper display
          const isVideo = file.type.startsWith('video');
          const thumbUUID = file.upload.uuid;
          
          if (isVideo) {
            // For videos, show placeholder image immediately after upload
            let newImageDataToAdd = {
              url: '../../../assets/images/no_image.jpg',
              finalPath: response.data.filePath,
              contentType: file.type,
              originalContentType: file.type,
              originTimestamp: moment().utc().format(),
              thumbUUID: thumbUUID
            };
            this.services.setServiceImagesToDeliver(originalJob.id, newImageDataToAdd);
            this.changeRef.markForCheck();
          } else if (!file.dataURL) {
            // For non-videos without dataURL, show placeholder
            let newImageDataToAdd = {
              url: thumbUrl || '../../../assets/images/no_image.jpg',
              finalPath: response.data.filePath,
              contentType: file.type,
              originalContentType: file.type,
              originTimestamp: moment().utc().format(),
              thumbUUID: thumbUUID
            };
            this.services.setServiceImagesToDeliver(originalJob.id, newImageDataToAdd);
            this.changeRef.markForCheck();
          } else {
            // For images with dataURL, use it directly
            const thumbUUID = file.upload.uuid;
            if (!thumbUrl) {
              this.makeTrustedImage(file.dataURL);
              let newImageDataToAdd = {
                url: file.dataURL,
                finalPath: response.data.filePath,
                contentType: file.type,
                originalContentType: file.type,
                originTimestamp: moment().utc().format(),
                thumbUUID: thumbUUID
              };
              this.services.setServiceImagesToDeliver(originalJob.id, newImageDataToAdd);

            } else {
              this.makeTrustedImage(thumbUrl);
              let newImageDataToAdd = {
                url: thumbUrl,
                finalPath: response.data.filePath,
                contentType: file.type,
                originalContentType: file.type,
                originTimestamp: moment().utc().format(),
                thumbUUID: thumbUUID
              };
              this.services.setServiceImagesToDeliver(originalJob.id, newImageDataToAdd);
            }
            this.changeRef.markForCheck();
          }

          this.JobUploaded = false;
          this.jobUploaderService.updateServiceUploaded(false);
          previewContainer.removeChild(progressElement);
          previewContainer.classList.remove('preview-uploading');
          this.jobUploaderService.setCurrentUpload(null, 100, true, file.name, file.upload.uuid, file.jobId);
        }).catch((error) => {
          console.error('Upload failed:', error);
          this.snackbarService.handleError('Upload failed. Please try again.');
          previewContainer.remove();
          this.jobUploaderService.setCurrentUpload(null, 0, false, file.name, file.upload.uuid, file.jobId);
        });
      },
      error: (error) => {
        console.error('Failed to get presigned URL:', error);
        this.snackbarService.handleError('Failed to prepare upload. Please try again.');
        previewContainer.remove();
      }
    });
  }

  deleteSelectedUploads() {
    const dialogRef = this.dialogService.openDialog(ConfirmModalComponent, {confirmMessage: 'Delete selected uploads?'}, {panelClass: 'base-modal-dialog'});

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.snackbarService.showSnackbar('Deleting uploads...');
        this.serviceUploadDeleteInProgress[this.localRefSelectedJob.id] = true;
        this.changeRef.markForCheck();
        const toDeleteFileFinalPaths = this.deleteSelectionCollection[this.localRefSelectedJob.id]
          .map(fileData => ({finalPath: fileData.finalPath}));

        this.jobUploaderService.deleteUploads(toDeleteFileFinalPaths)
          .toPromise()
          .then(() => {

            this.services.setServiceImagesToDeliver(this.localRefSelectedJob.id,
              this.deleteSelectionCollection[this.localRefSelectedJob.id],
              true);

            this.deleteSelectionCollection[this.localRefSelectedJob.id] = [];
            this.serviceUploadDeleteInProgress[this.localRefSelectedJob.id] = false;
            this.changeRef.markForCheck();
          })
          .catch(e => {
            console.error('Could not delete file:', e);
            this.snackbarService.handleError('Could not delete file');
            this.serviceUploadDeleteInProgress[this.localRefSelectedJob.id] = false;
          });
      }
    });


  }

  toggleUploadSelect(uploadFileData: any, changeEvent: MatCheckboxChange) {
    if (changeEvent.checked) {

      if (!this.deleteSelectionCollection[this.localRefSelectedJob.id]) {
        this.deleteSelectionCollection[this.localRefSelectedJob.id] = [];
      }

      this.deleteSelectionCollection[this.localRefSelectedJob.id].push(uploadFileData);
    } else {
      this.deleteSelectionCollection[this.localRefSelectedJob.id] = this.deleteSelectionCollection[this.localRefSelectedJob.id]
        .filter((fileItem) => {
          return fileItem.finalPath !== uploadFileData.finalPath;
        });
    }
  }

  setCanDeliver(event: { id: string, lastDateDelivered: string; canDeliver: boolean }) {
    const deliveryObjCopy = this.deliveryRef;

    deliveryObjCopy[event.id] = {
      canDeliver: event.canDeliver,
      lastDateDelivered: event.lastDateDelivered
    };

    this.deliveryRef = deliveryObjCopy;
  }

}

export class ServicesDataSource extends DataSource<(Service | undefined)> {
  private _length = 0;
  private _pageSize = 40;
  private _cachedData = [];
  private _fetchedPages = new Set<number>();
  private _dataStream = new BehaviorSubject<(Service | undefined)[]>(this._cachedData);
  private _subscription = new Subscription();
  thumbsLoaded = false;
  selectedService = null;
  searchString = '';
  requestViewUpdate$: EventEmitter<Service> = new EventEmitter();

  constructor(private servicesService: ServicesService,
              private changeDetector: ChangeDetectorRef,
              private processorService: ProcessorService) {
    super();
    this.processorService.processor$
      .subscribe(processor => {
        this._length = processor.jobsCount;
        this._cachedData.push({});
      });
  }

  private getContentTypeFromFileName(fileName: string): string {
    const extension = fileName.toLowerCase().split('.').pop();
    const contentTypeMap = {
      'mp4': 'video/mp4',
      'webm': 'video/webm',
      'ogg': 'video/ogg',
      'avi': 'video/x-msvideo',
      'mov': 'video/quicktime',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'pdf': 'application/pdf',
      'ai': 'application/postscript'
    };
    return contentTypeMap[extension] || 'application/octet-stream';
  }

  setServiceImagesToDeliver(serviceId, imageData, remove = false) {
    const foundSearchedServiceIndex = this._dataStream.value.findIndex(job => {
      return job.id === serviceId;
    });

    // STANDARD RESULTS THAT SHOULD BE SCANNED
    const foundCachedServiceIndex = this._cachedData.findIndex(job => {
      return job.id === serviceId;
    });

    if (remove) {
      // SEARCH RESULTS FILE COMPARISON
      if (!!this._dataStream.value[foundSearchedServiceIndex]) {
        this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver =
          difference(this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver, imageData);
        this._dataStream.next(this._dataStream.value.sort((a, b) => +b.received - +a.received));
      }

      // STANDARD RESULTS FILE COMPARISON
      if (!!this._cachedData[foundCachedServiceIndex]) {
        this._cachedData[foundCachedServiceIndex].imagesToDeliver =
          difference(this._cachedData[foundCachedServiceIndex].imagesToDeliver, imageData);
        this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
      }

      if (!!this.searchString) {
        if (!!this._cachedData[foundCachedServiceIndex]) {
          this._dataStream.value[foundSearchedServiceIndex] = this._cachedData[foundCachedServiceIndex];
        }

        this._dataStream.next(this._dataStream.value.sort((a, b) => +b.received - +a.received));
      } else {


        this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
      }

    } else {
      // STANDARD RESULTS FILE ADD
      if (!!this._cachedData[foundCachedServiceIndex]) {
        this._cachedData[foundCachedServiceIndex].imagesToDeliver.push(imageData);
      } else if (this._dataStream.value[foundSearchedServiceIndex]) {
        this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver.push(imageData);
      }
    }

    if (this._cachedData[foundCachedServiceIndex]) {
      if (this.selectedService.id === this._cachedData[foundCachedServiceIndex].id) {
        this.selectedService.imagesToDeliver = this._cachedData[foundCachedServiceIndex].imagesToDeliver;
        this.selectedService.images = this._cachedData[foundCachedServiceIndex].images;
      }
    } else if (this.selectedService.id === this._dataStream.value[foundSearchedServiceIndex].id) {
      this.selectedService.imagesToDeliver = this._dataStream.value[foundSearchedServiceIndex].imagesToDeliver;
      this.selectedService.images = this._dataStream.value[foundSearchedServiceIndex].images;
    }

    this.requestViewUpdate$.emit(this.selectedService);
  }

  connect(collectionViewer: CollectionViewer, initial = false): Observable<(Service | undefined)[]> {
    this._subscription.add(collectionViewer.viewChange.subscribe(range => {
      const startPage = this._getPageForIndex(range.start);
      const endPage = this._getPageForIndex(range.end - 1);
      for (let i = startPage; i <= endPage; i++) {
        this._fetchPage(i);
      }
    }));
    return this._dataStream;
  }

  disconnect(): void {
    this._subscription.unsubscribe();
  }

  setSelectedService(service) {
    this.selectedService = service;
    this.changeDetector.markForCheck();
  }

  private _getPageForIndex(index: number): number {
    return Math.ceil(index / this._pageSize);
  }

  private _fetchPage(page: number) {
    if (!this.searchString.length) {
      if (this._fetchedPages.has(page)) {
        return;
      }
      this._fetchedPages.add(page);
      this.makeServicesApiCall(page);
    }
  }

  setSearchString(searchString) {
    this.searchString = searchString;

  }

  setSearchResults(services, searchString) {
    this.searchString = searchString;
    if (services.length) {
      this._dataStream.next(services);
    } else if (!services.length && !!searchString) {
      this._dataStream.next([]);
    } else {
      this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
    }
  }

  setSearchResultFinals(services, finals) {
    if (finals) {
      const lastDeliveredObject = {};
      Object.entries(services).forEach(([serviceId, service]) => {
        const timelineEvents = Object
          .entries((<Service>service).timelineEvents || {
            [(<any>service).received]: {
              eventType: ServiceEventEnums.CREATION, date: (<any>service).received
            }
          });
        let lastDateDeliveredEntry = timelineEvents.reduce((acc, event) => {

          if ((<any>event[1]).eventType === ServiceEventEnums.DELIVERY) {

            if (!acc || moment(+acc[0]).isBefore(moment(+(<any>event[1]).date))) {
              acc = event;
            }
          }

          return acc;
        }, null);


        if (!lastDateDeliveredEntry) {
          lastDateDeliveredEntry = timelineEvents[0];
        }
        lastDeliveredObject[serviceId] = +lastDateDeliveredEntry[0];

      });

      Object.entries(finals).forEach(([serviceId, urls]) => {

        const found = this._dataStream.value.find(service => {
          if (!!service) {
            return service.id === serviceId;
          }
          return false;
        });

        if (!!found) {
          const [images, imagesToDeliver] = (<{ data, url }[]>urls).reduce((acc, urlItem) => {
            const assetNotDelivered = urlItem.data.notDelivered;
            
            // Fix contentType if backend returns incorrect value
            let contentType = urlItem.data.contentType;
            if (contentType === 'application/json' && urlItem.data.fileName) {
              contentType = this.getContentTypeFromFileName(urlItem.data.fileName);
            }

            if (assetNotDelivered) {
              acc[1].push({
                url: urlItem.url,
                ...urlItem.data,
                contentType: contentType
              });
            } else {
              acc[0].push({
                url: urlItem.url,
                ...urlItem.data,
                contentType: contentType
              });
            }

            return acc;
          }, [[], []]);

          found.images = images;
          found.imagesToDeliver = imagesToDeliver;

          this.thumbsLoaded = true;
        }

        this.changeDetector.detectChanges();
      });
    }
  }

  makeServicesApiCall(page, initial = false) {
    const source = this.servicesService.getServicesDetails({pageSize: this._pageSize, pageIndex: page});

    const final = source
      .pipe(
        switchMap(servicesResponse => {
          if (page === 0) {
            this._cachedData = [];
          }

          const jobs = Object.values(servicesResponse.data);
          this._cachedData = this._cachedData.concat(jobs);

          if (page === 0) {
            this.selectedService = this._cachedData[0];
          }
          if (!this.searchString) {
            this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
          }

          const finalDirectories = Object.values(servicesResponse.data).map((service: any) => service.fromDirectory);
          return this.servicesService.getServicesFinals({finalDirectories})
            .pipe(
              map(urlsResponse => {
                return [servicesResponse, urlsResponse];
              })
            );
        })
      );

    final.subscribe(
      ([servicesResponse, urlsResponse]) => {
        const lastDeliveredObject = {};
        Object.entries(servicesResponse.data).forEach(([serviceId, service]) => {

          const timelineEvents = Object.entries((<Service>service).timelineEvents || {
            [(<any>service).received]: {
              eventType: ServiceEventEnums.CREATION, date: (<any>service).received
            }
          });

          let lastDateDeliveredEntry = timelineEvents.reduce((acc, event) => {

            // Todo: Double check this
            if ((<any>event[1]).eventType === ServiceEventEnums.CREATION) {

              if (!acc || moment(+acc[0]).isBefore(moment(+(<any>event[1]).date))) {
                acc = event;
              }
            }

            return acc;
          }, null);


          if (!lastDateDeliveredEntry) {
            lastDateDeliveredEntry = timelineEvents[0];
          }
          lastDeliveredObject[serviceId] = +lastDateDeliveredEntry[0];

        });

        Object.entries(urlsResponse.data).forEach(([serviceId, urls]) => {
          const found = this._cachedData.find(service => {
            return service.id === serviceId;
          });

          const [images, imagesToDeliver] = (<{ data, url }[]>urls).reduce((acc, urlItem) => {
            const assetNotDelivered = urlItem.data.notDelivered;
            
            // Fix contentType if backend returns incorrect value
            let contentType = urlItem.data.contentType;
            if (contentType === 'application/json' && urlItem.data.fileName) {
              contentType = this.getContentTypeFromFileName(urlItem.data.fileName);
            }
            
            if (assetNotDelivered) {
              acc[1].push({
                url: urlItem.url,
                ...urlItem.data,
                contentType: contentType
              });
            } else {
              acc[0].push({
                url: urlItem.url,
                ...urlItem.data,
                contentType: contentType
              });
            }

            return acc;
          }, [[], []]);

          if (found) {
            found.images = images;
            found.imagesToDeliver = imagesToDeliver;
          }

          this.thumbsLoaded = true;
        });

        if (!initial && !this.searchString) {
          this._dataStream.next(this._cachedData.sort((a, b) => +b.received - +a.received));
        }
        this.changeDetector.detectChanges();
      },
      err => {
        console.error('Error with finals subscription:', err);
      },
      () => {
      }
    );


  }
}
